import { useCallback, useEffect, useState } from 'react';
import type { SellmonitorOAuthStatus } from '@ozon-ai/sellmonitor/contracts';
import { api } from '../../api/client';
import { integrationStatusLabel } from './settings-shared';

export interface ServiceStatus {
  ozon: { ok: boolean; error?: string };
  sellmonitor: { ok: boolean; error?: string };
  llm?: { ok: boolean; error?: string };
}

export function useSettingsIntegrationsStatus() {
  const [statusLoading, setStatusLoading] = useState(true);
  const [status, setStatus] = useState<ServiceStatus | null>(null);
  const [oauthStatus, setOauthStatus] = useState<SellmonitorOAuthStatus | null>(null);
  const [hasLlmKey, setHasLlmKey] = useState(false);

  const load = useCallback(async () => {
    setStatusLoading(true);
    const [statusResult, oauthResult, settingsResult] = await Promise.allSettled([
      api.get<ServiceStatus>('/settings/status'),
      api.get<SellmonitorOAuthStatus>('/sellmonitor/oauth/status'),
      api.get<{ secrets: { llm?: { apiKey?: string } } }>('/settings'),
    ]);

    if (statusResult.status === 'fulfilled') {
      setStatus(statusResult.value);
    } else {
      setStatus(null);
    }

    if (oauthResult.status === 'fulfilled') {
      setOauthStatus(oauthResult.value);
    } else {
      setOauthStatus(null);
    }

    if (settingsResult.status === 'fulfilled') {
      setHasLlmKey(!!settingsResult.value?.secrets.llm?.apiKey);
    } else {
      setHasLlmKey(false);
    }

    setStatusLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const llmSubtitle = statusLoading
    ? 'Проверка…'
    : hasLlmKey
      ? integrationStatusLabel(status?.llm?.ok, status?.llm?.error)
      : 'Не настроен';

  const ozonSubtitle = statusLoading
    ? 'Проверка…'
    : integrationStatusLabel(status?.ozon.ok, status?.ozon.error);

  const sellmonitorConnected = oauthStatus?.connected || status?.sellmonitor.ok;
  const sellmonitorSubtitle = statusLoading
    ? 'Проверка…'
    : sellmonitorConnected
      ? oauthStatus?.connected
        ? 'OAuth'
        : 'Подключён'
      : oauthStatus?.needsRefresh
        ? 'Токен истёк'
        : integrationStatusLabel(status?.sellmonitor.ok, status?.sellmonitor.error);

  return {
    statusLoading,
    reload: load,
    ozonSubtitle,
    sellmonitorSubtitle,
    llmSubtitle,
  };
}
